import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { logError } from '@/hooks/useErrorLogging';

/** sessionStorage key for the tap → Index handoff. Value: JSON {target, at}. */
export const PUSH_TAP_TARGET_KEY = 'clando.pushTap.target';

/**
 * Routes native push-notification taps to their deep link.
 *
 * FCM system notifications are rendered by Android; when the user taps one,
 * Capacitor fires `pushNotificationActionPerformed` with the data payload.
 * Without a listener the app simply resumes wherever it was. The web
 * equivalent is handled by the service worker's notificationclick handler.
 *
 * Delivery is belt-and-suspenders:
 *  1. navigate(target) — works when the router is already settled (warm app).
 *  2. sessionStorage handoff (ShareTarget pattern) — survives the cold-start
 *     races where AuthGate / shell routing re-navigate during boot and wipe
 *     the URL params before Index can consume them. Index reads the key on
 *     mount and applies it; entries older than 60s are ignored so a stale
 *     handoff can't reopen a task modal on a later unrelated launch.
 *
 * Both paths drop a `push_tap` breadcrumb in device_error_logs so tap
 * behavior is remotely diagnosable via the telemetry_errors MCP tool.
 */
export function PushTapRouter() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = (action.notification?.data ?? {}) as Record<string, string>;

      let target = data.url || '';
      if (target.startsWith('http')) {
        // Absolute URLs: keep SPA-internal navigation instead of a reload.
        try {
          const u = new URL(target);
          target = u.pathname + u.search;
        } catch {
          target = '';
        }
      }
      if (!target) {
        // Older payloads carried only the task fields, no url.
        const taskId = data.task || data.taskId;
        target = taskId ? `/?tab=tasks&task=${taskId}` : '/';
      }

      logError('push_tap', {
        kind: 'breadcrumb',
        stage: 'action_performed',
        target,
        rawData: data,
      });

      try {
        sessionStorage.setItem(PUSH_TAP_TARGET_KEY, JSON.stringify({ target, at: Date.now() }));
      } catch {
        /* sessionStorage blocked — navigate() below is the only path */
      }
      navigate(target);
    });

    return () => {
      try {
        (listener as unknown as { remove: () => void }).remove();
      } catch {
        /* listener already gone */
      }
    };
  }, [navigate]);

  return null;
}
